import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { BookOpen, FileText, ArrowRight, GraduationCap } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import Seo from '../components/Seo';
import { VOCAB_HUB, topicsByLevel, topicPath } from '../config/vocabulario';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = 'https://spanischenlared.com';

const LEVEL_BADGE = {
  A1: 'bg-green-100 text-green-700 border-green-300',
  A2: 'bg-blue-100 text-blue-700 border-blue-300',
};

const TopicCard = ({ topic, index, hasPdf }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.3) }}
    whileHover={{ y: -6 }}
    data-testid={`vocab-card-${topic.slug}`}
    className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 flex flex-col"
  >
    <div className="flex items-center justify-between mb-4">
      <span className={`px-3 py-1 rounded-full text-xs font-bold border ${LEVEL_BADGE[topic.level]}`}>
        {topic.level}
      </span>
      {hasPdf && (
        <span className="text-xs text-gray-500 flex items-center gap-1">
          <FileText className="w-3.5 h-3.5" />
          Incluye PDF descargable
        </span>
      )}
    </div>
    <h3 className="text-lg font-bold text-[#4A4A4A] mb-3">{topic.title}</h3>
    <p className="text-sm text-gray-600 mb-6 flex-1">{topic.short}</p>
    <Link
      to={topicPath(topic.slug)}
      data-testid={`vocab-link-${topic.slug}`}
      className="w-full py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all duration-300"
    >
      <BookOpen className="w-4 h-4" />
      Ver vocabulario
    </Link>
  </motion.div>
);

export default function VocabularioHubPage() {
  const [pdfs, setPdfs] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/vocabulario/pdfs`).then(({ data }) => setPdfs(data.available || [])).catch(() => {});
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Inicio', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Recursos', item: `${SITE}/#resources` },
          { '@type': 'ListItem', position: 3, name: 'Vocabulario', item: `${SITE}${VOCAB_HUB.path}` },
        ],
      },
      {
        '@type': 'WebPage',
        name: VOCAB_HUB.seoTitle,
        description: VOCAB_HUB.seoDescription,
        url: `${SITE}${VOCAB_HUB.path}`,
        inLanguage: 'es',
      },
    ],
  };

  const sections = [
    { level: 'A1', title: 'Vocabulario A1', text: 'Temas esenciales para empezar a comunicarte desde tu primera clase.' },
    { level: 'A2', title: 'Vocabulario A2', text: 'Situaciones cotidianas para desenvolverte con más soltura en español.' },
  ];

  return (
    <div className="min-h-screen bg-white text-[#4A4A4A]">
      <Seo path={VOCAB_HUB.path} fullTitle={VOCAB_HUB.seoTitle} description={VOCAB_HUB.seoDescription} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navigation />

      <section className="pt-32 pb-12 bg-gradient-to-b from-[#E8F5FA] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-xs text-gray-500 mb-6" data-testid="vocab-breadcrumb">
            <Link to="/" className="hover:text-[#7DBDD9]">Inicio</Link>
            <span className="mx-2">/</span>
            <Link to="/#resources" className="hover:text-[#7DBDD9]">Recursos</Link>
            <span className="mx-2">/</span>
            <span className="text-[#4A4A4A] font-semibold">Vocabulario</span>
          </nav>
          <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">Recursos gratuitos</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#4A4A4A] mt-4 mb-6" data-testid="vocab-hub-h1">
            {VOCAB_HUB.h1}
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl leading-relaxed">
            Aquí tienes el vocabulario de español organizado por temas y por niveles. Cada ficha incluye las
            palabras más útiles con su traducción al <strong>inglés</strong> y al <strong>alemán</strong>, frases
            de ejemplo y una ficha en PDF para descargar gratis. Elige el tema que necesites y
            empieza a practicar.
          </p>
        </div>
      </section>

      {sections.map(({ level, title, text }) => (
        <section key={level} className="py-12" id={`vocabulario-${level.toLowerCase()}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-base md:text-lg font-bold text-[#4A4A4A] uppercase tracking-wider mb-2">{title}</h2>
            <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-4" />
            <p className="text-sm text-gray-600 mb-8 max-w-2xl">{text}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {topicsByLevel(level).map((topic, i) => (
                <TopicCard key={topic.slug} topic={topic} index={i} hasPdf={pdfs.includes(topic.slug)} />
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="bg-gradient-to-r from-[#E8F5FA] to-[#FFF0EF] rounded-3xl p-8 shadow-lg">
            <GraduationCap className="w-10 h-10 text-[#7DBDD9] mx-auto mb-4" />
            <h2 className="text-base md:text-lg font-bold text-[#4A4A4A] mb-2">
              ¿No sabes cuál es tu nivel de español?
            </h2>
            <p className="text-sm text-gray-600 mb-6">Haz el test de nivel gratuito y descúbrelo en 5 minutos.</p>
            <Link
              to="/test-de-nivel"
              data-testid="vocab-hub-test-cta"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
            >
              Hacer test de nivel
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
